import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { startLabSession, endLabSession } from "../../lib/functions";

const TermWrap = styled.div`
  width: 100%;
  height: 60vh;
  background: #000;
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  padding: 8px;
`;

const Notice = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-disabled);
  letter-spacing: 0.06em;
`;

const LabTerminal = ({ labId }) => {
  const wrapRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | unavailable | connected | error
  const sessionRef = useRef(null);

  useEffect(() => {
    let term, fit, socket, disposed = false;
    (async () => {
      let res;
      try {
        res = await startLabSession({ labId });
      } catch (e) {
        if (!disposed) setStatus("error");
        return;
      }
      const data = res && res.data ? res.data : res;
      sessionRef.current = data.sessionId;
      const wsBase = data.wsBase || process.env.GATSBY_LAB_WS_BASE || "";
      if (!wsBase) { if (!disposed) setStatus("unavailable"); return; }

      const { Terminal } = await import("xterm");
      const { FitAddon } = await import("xterm-addon-fit");
      await import("xterm/css/xterm.css");
      if (disposed) return;
      term = new Terminal({ cursorBlink: true, fontFamily: "'Space Mono', monospace", fontSize: 13 });
      fit = new FitAddon();
      term.loadAddon(fit);
      term.open(wrapRef.current);
      fit.fit();

      socket = new WebSocket(`${wsBase}?token=${encodeURIComponent(data.token)}`);
      socket.binaryType = "arraybuffer";
      socket.onopen = () => { if (!disposed) setStatus("connected"); };
      // The gateway streams pod stdout as binary WebSocket frames (raw terminal
      // bytes); status/progress lines arrive as text. xterm.write accepts both a
      // string and a Uint8Array (it buffers partial UTF-8 across chunks).
      socket.onmessage = (ev) => {
        if (typeof ev.data === "string") term.write(ev.data);
        else term.write(new Uint8Array(ev.data));
      };
      socket.onerror = () => { if (!disposed) setStatus("error"); };
      socket.onclose = () => { if (!disposed) setStatus("error"); };
      term.onData((d) => { if (socket.readyState === 1) socket.send(d); });
      const onResize = () => { try { fit.fit(); } catch (_) {} };
      window.addEventListener("resize", onResize);
      term._ndCleanup = () => window.removeEventListener("resize", onResize);
    })();

    return () => {
      disposed = true;
      try { if (socket) socket.close(); } catch (_) {}
      try { if (term && term._ndCleanup) term._ndCleanup(); if (term) term.dispose(); } catch (_) {}
      if (sessionRef.current) { endLabSession({ sessionId: sessionRef.current }).catch(() => {}); }
    };
  }, [labId]);

  if (status === "unavailable") return <Notice>[ LAB ENVIRONMENT NOT YET AVAILABLE ]</Notice>;
  if (status === "error") return <Notice>[ COULD NOT START LAB — TRY AGAIN ]</Notice>;
  return <TermWrap ref={wrapRef} aria-label="lab terminal" />;
};

export default LabTerminal;
